import { Link } from "react-router-dom";
import { Btn } from "@/components/ui-kit";
import PublicFooter from "@/components/PublicFooter";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/auth";
import { api, fileUrl } from "@/api";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useState, useEffect } from "react";

const CREATORS = [
  { name: "Emma L", loc: "Helsinki", tag: "Lifestyle", img: "/brand/creators/creator-1.webp" },
  { name: "Aleksi P", loc: "Tampere", tag: "Lifestyle", img: "/brand/creators/creator-2.webp" },
  { name: "Liina S", loc: "Etä", tag: "Kauneus", img: "/brand/creators/creator-3.webp" },
  { name: "Sonja T", loc: "Etä", tag: "Muoti", img: "/brand/creators/creator-4.webp" },
  { name: "Johanna K", loc: "Helsinki", tag: "Lifestyle", img: "/brand/creators/creator-5.webp" },
  { name: "Mila R", loc: "Oulu", tag: "Kauneus", img: "/brand/creators/creator-6.webp" },
];

const FAQ = [
  { q: "Onko Creatori ilmainen?", a: "Kyllä, MVP-vaiheessa palvelun peruskäyttö on täysin maksuton sekä yrityksille että sisällöntuottajille." },
  { q: "Kuka voi liittyä sisällöntuottajaksi?", a: "Kuka tahansa somevaikuttaja, bloggaaja tai sisällöntekijä voi luoda profiilin ja hakea avoimiin kampanjoihin." },
  { q: "Miten yritys löytää sopivat sisällöntuottajat?", a: "Yritykset voivat selata sisällöntuottajien profiileja tai julkaista kampanjan, johon sisällöntuottajat hakevat." },
  { q: "Tuleeko palveluun maksuja tai Premium-ominaisuuksia?", a: "Kyllä. Creatori on rakennettu laajennettavaksi – Premium-jäsenyydet, maksut ja AI-ominaisuudet ovat tulossa." },
];

function Nav() {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-50 bg-white border-b border-[#E6E1D9]">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-link"><Logo variant="light" className="h-6 w-auto" /></Link>
        <nav className="hidden md:flex items-center gap-9 text-sm text-[#292827]">
          <a href="#miten" className="hover:opacity-60 transition-opacity">Miten toimii</a>
          <a href="#tuottajat" className="hover:opacity-60 transition-opacity">Sisällöntuottajat</a>
          <a href="#faq" className="hover:opacity-60 transition-opacity">UKK</a>
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <Btn to="/dashboard" variant="sm" data-testid="nav-dashboard">Dashboard</Btn>
          ) : (
            <>
              <Link to="/kirjaudu" className="text-sm font-medium text-[#292827] hover:opacity-60 transition" data-testid="nav-login">Kirjaudu</Link>
              <Btn to="/valitse-rooli" variant="sm" data-testid="nav-register">Aloita</Btn>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function CreatorPreview({ c }) {
  return (
    <div className="group">
      <div className="relative overflow-hidden rounded-2xl border border-[#E6E1D9] aspect-[4/5]">
        <img src={c.img} alt={c.name} loading="lazy" decoding="async" width="400" height="500" sizes="(min-width:1024px) 25vw, (min-width:640px) 50vw, 100vw" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
        <span className="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-full px-3 py-1 text-xs font-medium text-[#292827]">{c.tag}</span>
      </div>
      <div className="mt-3 flex items-baseline justify-between">
        <p className="text-[15px] font-medium text-[#292827]">{c.name}</p>
        <p className="text-[13px] text-[#968F85]">{c.loc}</p>
      </div>
    </div>
  );
}

const AUDIENCE = [
  { id: "business", label: "YRITYKSILLE", title: "Tarvitsetko sisällöntuottajia?", text: "Etsitkö UGC-tekijää, sisällöntuottajaa tai vaikuttajaa yrityksen markkinointiin? Julkaise hakukampanja silloin, kun haluat, tutustu hakijoihin ja valitse sopivimmat tekijät.", bold: "Ensimmäinen kampanja maksutta.", cta: "Julkaise yrityksen kampanja", to: "/valitse-rooli?rooli=business" },
  { id: "creator", label: "SISÄLLÖNTUOTTAJILLE", title: "Löydä uusia yhteistyömahdollisuuksia.", text: "Tutustu kiinnostaviin kampanjoihin, esittele osaamistasi ja hae mukaan yritysten yhteistyöprojekteihin.", bold: "Rekisteröidy maksutta.", cta: "Liity sisällöntuottajaksi", to: "/valitse-rooli?rooli=creator" },
];

function AudienceIntro({ a }) {
  return (
    <div data-testid={`audience-card-${a.id}`} className="flex flex-col">
      <p className="text-xs font-semibold tracking-[0.14em] text-[#968F85]" data-testid={`audience-label-${a.id}`}>{a.label}</p>
      <h3 className="mt-3 text-xl font-semibold tracking-tightish text-[#292827]">{a.title}</h3>
      <p className="mt-3 text-[15px] text-[#6E675C] leading-relaxed">{a.text}</p>
      <p className="mt-3 text-[15px] font-semibold text-[#292827]">{a.bold}</p>
      <Link to={a.to} data-testid={`audience-link-${a.id}`} className="mt-5 self-start text-[15px] font-medium text-[#292827] underline underline-offset-4 decoration-[#D5E5F9] decoration-2 hover:decoration-[#292827] transition-colors">{a.cta} →</Link>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <button className="w-full text-left border-b border-[#E6E1D9] py-6" onClick={() => setOpen(!open)} data-testid="faq-item">
      <div className="flex items-center justify-between gap-4">
        <h4 className="text-[17px] font-medium text-[#292827] tracking-tightish">{q}</h4>
        <ArrowRight className={`shrink-0 text-[#968F85] transition-transform duration-300 ${open ? "rotate-90 text-[#292827]" : ""}`} size={17} />
      </div>
      {open && <p className="mt-4 text-[15px] text-[#6E675C] leading-relaxed max-w-2xl">{a}</p>}
    </button>
  );
}

export default function Landing() {
  const [realCreators, setRealCreators] = useState([]);
  useEffect(() => {
    api.get("/public/landing-creators")
      .then((r) => setRealCreators((r.data.creators || []).map((c) => ({
        _id: c.user_id,
        name: c.name,
        loc: c.loc,
        tag: c.tag || "Sisällöntuottaja",
        img: fileUrl(c.img),
      }))))
      .catch(() => setRealCreators([]));
  }, []);
  const SLOTS = 4;
  const shown = realCreators.length >= SLOTS
    ? realCreators.slice(0, SLOTS)
    : [...realCreators, ...CREATORS.slice(0, SLOTS - realCreators.length)];
  return (
    <div className="min-h-screen bg-white">
      <Nav />

      {/* Hero content */}
      <section className="max-w-5xl mx-auto px-6 pt-14 md:pt-20 pb-20 text-center">
        <div className="animate-fade-up">
          <h1 className="text-[clamp(2rem,10.5vw,2.75rem)] sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-display text-[#292827] leading-[1.05] max-w-4xl mx-auto break-words">
            Yritysten ja sisällöntuottajien kohtaamispaikka
          </h1>
          <p className="mt-7 text-lg md:text-xl text-[#6E675C] leading-relaxed max-w-xl mx-auto">
            Creatori tuo yritykset ja sisällöntuottajat yhteen ja tekee yhteistyön aloittamisesta helppoa.
          </p>
          <div className="mt-8 md:mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Btn to="/valitse-rooli?rooli=business" variant="primary" data-testid="hero-cta-business">Rekisteröidy yrityksenä</Btn>
            <Btn to="/valitse-rooli?rooli=creator" variant="dark" data-testid="hero-cta-creator">Olen sisällöntuottaja</Btn>
          </div>
          <div className="mt-16 md:mt-20 pt-12 border-t border-[#E6E1D9] grid md:grid-cols-2 gap-12 md:gap-16 text-left">
            {AUDIENCE.map((a) => <AudienceIntro key={a.id} a={a} />)}
          </div>
        </div>
      </section>

      {/* Creator showcase */}
      <section id="tuottajat" className="brand-gradient">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-20 md:py-24">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-[clamp(1.5rem,7.6vw,1.875rem)] md:text-4xl font-semibold tracking-display text-[#292827] whitespace-nowrap" data-testid="creator-count">+1000 <span className="font-medium text-[#5B7FB0]">sisällöntuottajaa</span></p>
              <h2 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tightish text-[#292827]">Tutustu tekijöihin</h2>
            </div>
            <Link to="/valitse-rooli?rooli=business" className="hidden sm:flex items-center gap-1 text-sm font-medium text-[#292827] hover:opacity-60 transition">
              Selaa kaikkia <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {shown.map((c) => <CreatorPreview key={c._id || c.name} c={c} />)}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="miten" className="max-w-6xl mx-auto px-6 py-24">
        <div className="max-w-xl">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tightish text-[#292827]">Kolme yksinkertaista vaihetta</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {[
            { n: "1", t: "Luo profiili", d: "Rekisteröidy yrityksenä tai sisällöntuottajana ja täydennä profiilisi." },
            { n: "2", t: "Löydä osumat", d: "Yritykset julkaisevat kampanjoita, sisällöntuottajat hakevat kiinnostaviin yhteistöihin." },
            { n: "3", t: "Tee yhteistyötä", d: "Yritykset käsittelevät hakemukset, ottavat yhteyttä valittuun sisällöntuottajaan ja hoidatte yhteistyöhön liittyvät sopimukset sekä muut yksityiskohdat yhdessä." },
          ].map((s) => (
            <div
              key={s.n}
              data-testid={`step-card-${s.n}`}
              className="group relative rounded-3xl border border-[#E6E1D9] bg-white p-8 soft-shadow hover:soft-shadow-hover hover:-translate-y-1 transition-[transform,box-shadow] duration-300 ease-out"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#D5E5F9] text-[#292827] text-lg font-semibold tracking-tightish transition-colors duration-300 group-hover:bg-[#292827] group-hover:text-white">
                {s.n}
              </div>
              <h3 className="mt-6 text-xl font-semibold tracking-tightish text-[#292827]">{s.t}</h3>
              <p className="mt-2.5 text-[15px] text-[#6E675C] leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-24">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tightish text-[#292827]">Usein kysytyt kysymykset</h2>
        <div className="mt-8">{FAQ.map((f, i) => <FaqItem key={i} {...f} />)}</div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-24 pt-8">
        <div className="rounded-[2rem] bg-[#D5E5F9] px-6 py-20 md:py-24 text-center">
          <h2 className="text-3xl md:text-5xl font-semibold tracking-display text-[#292827] leading-[1.05] max-w-2xl mx-auto">
            Aloita maksutta jo tänään
          </h2>
          <p className="mt-5 text-lg text-[#3A3733] max-w-xl mx-auto">Rekisteröityminen on täysin ilmaista sekä yrityksille että sisällöntuottajille. Luo ilmainen tili ja löydä uusia yhteistyömahdollisuuksia.</p>
          <div className="mt-9 flex justify-center">
            <Btn to="/valitse-rooli" variant="secondary" data-testid="cta-create-account">Luo ilmainen tili</Btn>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
